import {
  Box,
  Button,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Link,
  Tooltip,
} from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import { CalendarClock, ChevronDown } from "lucide-react";
import { AssignmentOpportunity } from "protogen/assignments_pb";
import { useEffect, useState } from "react";
import { feeSchedule } from "components/family/utils";
import { PlatformFeeSchedule } from "protogen/billing_pb";

const getFeeScheduleDescription = (schedule: PlatformFeeSchedule): string => {
  switch (schedule) {
    case PlatformFeeSchedule.SCHEDULE_LEAD_GEN_43_43_43_30:
    case PlatformFeeSchedule.SCHEDULE_LEAD_GEN_35_35_35_20:
    case PlatformFeeSchedule.SCHEDULE_LEAD_GEN_30_30_30_10:
    case PlatformFeeSchedule.SCHEDULE_LEAD_GEN_30_30_30_15:
    case PlatformFeeSchedule.SCHEDULE_LEAD_GEN_45_45_45_25:
      return "Fees for months 1, 2, 3, then ongoing";
    case PlatformFeeSchedule.SCHEDULE_FLAT_10:
    case PlatformFeeSchedule.SCHEDULE_FLAT_15:
    case PlatformFeeSchedule.SCHEDULE_FLAT_20:
    case PlatformFeeSchedule.SCHEDULE_FLAT_25:
      return "Flat fee each month";
    case PlatformFeeSchedule.SCHEDULE_NO_FEE:
      return "No fee";
    default:
      return "";
  }
};

const getRematchStartLabel = (
  assignmentOpportunity: AssignmentOpportunity,
): string => {
  if (assignmentOpportunity.immediateRematch) {
    return "Starts immediately";
  }
  if (!assignmentOpportunity.expectedStartAtSec) {
    return "Starts at the end of the client’s current billing cycle";
  }
  const expectedStart = new Date(
    Number(assignmentOpportunity.expectedStartAtSec) * 1000,
  ).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return `Expected start: ${expectedStart} (end of current billing cycle)`;
};

interface AssignmentActionsProps {
  disabled?: boolean;
  isRematch?: boolean;
  onAccept: () => void;
  onDecline: (reason: string | null) => void;
}

const AssignmentActions = ({
  disabled,
  isRematch,
  onAccept,
  onDecline,
}: AssignmentActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleDeclineClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDeclineClose = (reason?: string) => {
    setAnchorEl(null);
    if (reason) {
      onDecline(reason === "Other" ? null : reason);
    }
  };

  if (isRematch) {
    return (
      <Box sx={{ display: "flex", gap: "16px" }}>
        <Button
          variant="contained"
          onClick={onAccept}
          disabled={disabled}
          sx={{ flex: 1 }}
        >
          I’m interested
        </Button>
        <Button
          variant="outlined"
          onClick={() => onDecline(null)}
          disabled={disabled}
          sx={{ flex: 1 }}
        >
          Not interested
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: "16px" }}>
      <Button
        variant="contained"
        onClick={onAccept}
        disabled={disabled}
        sx={{ flex: 1 }}
      >
        Accept
      </Button>
      <Button
        variant="outlined"
        onClick={handleDeclineClick}
        disabled={disabled}
        sx={{ flex: 1 }}
        endIcon={<ChevronDown />}
      >
        Decline
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleDeclineClose()}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleDeclineClose("Not a good fit")}>
          Not a good fit
        </MenuItem>
        <MenuItem onClick={() => handleDeclineClose("Not a good time")}>
          Not a good time
        </MenuItem>
        <MenuItem onClick={() => handleDeclineClose("Too expensive")}>
          Too expensive
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeclineClose("Other")}>Other</MenuItem>
      </Menu>
    </Box>
  );
};

interface ClientOpportunityProps {
  disabled?: boolean;
  assignmentOpportunity: AssignmentOpportunity;
  senderView: boolean;
  onAccept: () => void;
  onDecline: (reason: string | null) => void;
}

export default ({
  disabled,
  assignmentOpportunity,
  senderView,
  onAccept,
  onDecline,
}: ClientOpportunityProps) => {
  const isMobile = useIsMobile();
  const isRematch = assignmentOpportunity.entityType === "rematch_session";
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isExpired, setIsExpired] = useState<boolean>(
    assignmentOpportunity.state === "timed-out",
  );

  const location = assignmentOpportunity.location || "";
  const approximateDistanceMiles =
    assignmentOpportunity.approximateDistanceMiles || "";
  const description = assignmentOpportunity.description || "";
  const rate = assignmentOpportunity.monthlyRateCents
    ? `$${assignmentOpportunity.monthlyRateCents / 100}/mo`
    : "";
  let hours = "";
  if (assignmentOpportunity.isPayAsYouGo) {
    hours = "Pay as you go at $50/hr";
  } else if (assignmentOpportunity.monthlyHours) {
    hours = `${assignmentOpportunity.monthlyHours} hours`;
  }
  const platformFee =
    assignmentOpportunity.platformFeeSchedule !== null &&
    assignmentOpportunity.platformFeeSchedule !==
      PlatformFeeSchedule.SCHEDULE_NOT_SET
      ? feeSchedule(assignmentOpportunity.platformFeeSchedule)
      : "";
  const platformFeeDescription =
    assignmentOpportunity.platformFeeSchedule !==
    PlatformFeeSchedule.SCHEDULE_NOT_SET
      ? getFeeScheduleDescription(assignmentOpportunity.platformFeeSchedule)
      : "";
  const expiresAt = assignmentOpportunity.expiresAtSec
    ? new Date(Number(assignmentOpportunity.expiresAtSec) * 1000)
    : null;

  const isDisabled =
    disabled || isExpired || assignmentOpportunity.state !== "active-waiting";

  useEffect(() => {
    if (isDisabled || !expiresAt) return;
    const updateTimer = () => {
      const now = new Date();
      const diff = Math.floor((expiresAt.getTime() - now.getTime()) / 1000);
      setTimeRemaining(diff);
      setIsExpired(diff <= 0);
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  const formatTimeRemaining = () => {
    if (isExpired) return "Expired";

    const hours = Math.floor(timeRemaining / 3600);
    const minutes = Math.floor((timeRemaining % 3600) / 60);
    const seconds = timeRemaining % 60;

    let timeStr = "";
    if (hours > 0) {
      timeStr = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      timeStr = `${minutes}m ${seconds}s`;
    } else {
      timeStr = `${seconds}s`;
    }

    return isMobile ? `Expires ${timeStr}` : `Expires in ${timeStr}`;
  };

  return (
    <Box
      sx={{
        borderRadius: "16px",
        width: "100%",
        maxWidth: "600px",
        opacity:
          isExpired || assignmentOpportunity.state === "cancelled" ? 0.7 : 1,
        minWidth: isMobile ? "90vw" : "500px",
        border: "1px solid #D4D4D4",
        overflow: "hidden",
        padding: isMobile ? "16px 20px" : "20px 24px 24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
        }}
      >
        <Typography variant="h4">
          {isRematch ? "Rematch Opportunity" : "Client Opportunity"}
        </Typography>
        {assignmentOpportunity.state === "accepted" && (
          <Typography variant="bodyHeavy" color="success.main">
            Accepted
          </Typography>
        )}
        {assignmentOpportunity.state === "declined" && (
          <Typography color={"error.main"} variant="bodySmallHeavy">
            {assignmentOpportunity.declinedReason === "skipped"
              ? "Expired"
              : "Declined"}
          </Typography>
        )}
        {assignmentOpportunity.state === "cancelled" && (
          <Typography color={"error.main"} variant="bodySmallHeavy">
            Expired
          </Typography>
        )}
        {assignmentOpportunity.state === "active-waiting" ||
        assignmentOpportunity.state === "timed-out" ? (
          <Typography
            variant="bodySmall"
            color={isExpired ? "error.main" : "text.secondary"}
            sx={{ fontWeight: isExpired ? "bold" : "500" }}
          >
            {formatTimeRemaining()}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <Typography variant="bodyHeavy">{location}</Typography>
            {approximateDistanceMiles && (
              <Typography variant="bodySmall">
                ({approximateDistanceMiles})
              </Typography>
            )}
          </Box>
          {(rate || hours) && (
            <Typography variant="bodyHeavy">
              {[rate, hours].filter(Boolean).join(", ")}
            </Typography>
          )}
          {platformFee && (
            <Tooltip title={platformFeeDescription} arrow placement="top">
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{
                  cursor: "help",
                  width: "fit-content",
                }}
              >
                Fee: {platformFee}
              </Typography>
            </Tooltip>
          )}
          {isRematch && (
            <Box
              sx={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                marginTop: "8px",
              }}
            >
              <CalendarClock size={16} />
              <Typography variant="bodySmallHeavy">
                {getRematchStartLabel(assignmentOpportunity)}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography
          variant="body"
          sx={{
            whiteSpace: "pre-line",
          }}
        >
          {description}
        </Typography>
        {senderView && !isRematch && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {assignmentOpportunity.state === "accepted" &&
            assignmentOpportunity.familyRef ? (
              <Link
                href={`/families/${encodeURIComponent(
                  assignmentOpportunity.familyRef,
                )}`}
              >
                View {assignmentOpportunity.familyName} family
              </Link>
            ) : (
              <Link
                href={`/tools/matchmaking/${encodeURIComponent(
                  assignmentOpportunity.signupRef,
                )}`}
              >
                View lead
              </Link>
            )}
          </Box>
        )}
        {!senderView && (
          <>
            {assignmentOpportunity.state === "accepted" &&
              assignmentOpportunity.familyRef && (
                <Link
                  href={`/families/${encodeURIComponent(
                    assignmentOpportunity.familyRef,
                  )}`}
                >
                  View {assignmentOpportunity.familyName} family
                </Link>
              )}
            {assignmentOpportunity.state === "active-waiting" && (
              <>
                {isRematch ? (
                  <Box>
                    <Divider sx={{ marginBottom: "20px" }} />
                    <Typography variant="bodyHeavy">
                      Interested in working with this client?
                    </Typography>
                    <Typography variant="body" color="text.secondary">
                      We’ll share your profile along with a few other Advisors
                      and let you know if they choose you.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="bodySmall" color="text.secondary">
                      By accepting this client, you are agreeing to our{" "}
                      <Link
                        href="https://advisor.findfaye.com/documents/d7cfe03f-10aa-42cb-a60e-ea488220bcb2"
                        target="_blank"
                      >
                        Faye lead terms
                      </Link>{" "}
                      including:
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        margin: "8px 0 0 0",
                        paddingLeft: "20px",
                        listStyleType: "disc",
                        "& li": { marginBottom: "4px", display: "list-item" },
                      }}
                    >
                      <Typography
                        component="li"
                        variant="bodySmall"
                        color="text.secondary"
                      >
                        Initial outreach within 1 hr of accepting lead (during
                        waking hrs)
                      </Typography>
                      <Typography
                        component="li"
                        variant="bodySmall"
                        color="text.secondary"
                      >
                        Availability to hold onboarding call within 48 hrs of
                        match
                      </Typography>
                      <Typography
                        component="li"
                        variant="bodySmall"
                        color="text.secondary"
                      >
                        A minimum 60-day service commitment
                      </Typography>
                    </Box>
                  </Box>
                )}
                <AssignmentActions
                  disabled={isDisabled}
                  isRematch={isRematch}
                  onAccept={onAccept}
                  onDecline={onDecline}
                />
              </>
            )}
            {assignmentOpportunity.state === "declined" && (
              <Box sx={{ display: "flex" }}>
                <Typography variant="bodySmall">
                  Taking a break from new clients? You can always toggle these
                  opportunities off in your{" "}
                  <Link href="/settings">settings</Link>.
                </Typography>
              </Box>
            )}
            {isExpired && (
              <Box sx={{ display: "flex" }}>
                <Typography variant="bodySmall">
                  If you're still interested in this opportunity, reply below to
                  let us know and check if the client is still available.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
